// @ts-strict-ignore
import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import type { State } from '~/store';
import {
  filterActionsByType,
  isDefaultPrecedenceIndexedAction,
  isPreviewOutcomeAction,
  isPreviewWorkflowRoutingAction,
} from '../../../service/actions';
import { isPublishedAttribute } from '../../../service/publishedAttributes';
import { ActionType } from '../../engine/constants';
import type { Action, PublicId } from '../../engine/types';
import { selectInitialActions } from './history';


export const selectOutcomeReasonActions = createSelector(
  [
    (state: State) => state.workflowEditor.workflowData?.actions,
  ],
  actions => filterActionsByType(actions, ActionType.OUTCOME_REASON),
);

export const selectOutcomeActions = createSelector(
  [
    (state: State) => state.workflowEditor.workflowData?.actions,
  ],
  actions => filterActionsByType(actions, ActionType.OUTCOME),
);

export const selectInitialOutcomeActions = createSelector(
  [
    selectInitialActions,
  ],
  actions => filterActionsByType(actions, ActionType.OUTCOME),
);

/**
 * Union of the initial and current outcome actions, keyed by publicId and preferring the
 * current copy of an outcome that exists on both sides.
 */
export const selectMergedOutcomeActions = createSelector(
  [
    selectInitialOutcomeActions,
    selectOutcomeActions,
  ],
  (initialOutcomeActions, currentOutcomeActions) => {
    const mergedById = new Map<PublicId, Action>();

    for (const action of [...initialOutcomeActions, ...currentOutcomeActions]) {
      mergedById.set(action.publicId, action);
    }

    return [...mergedById.values()];
  },
);

export const selectWorkflowRoutingActions = createSelector(
  [
    (state: State) => state.workflowEditor.workflowData?.actions,
  ],
  actions => filterActionsByType(actions, ActionType.WORKFLOW),
);

export const selectMatrixModelActions = createSelector(
  [
    (state: State) => state.workflowEditor.workflowData?.actions,
  ],
  actions => filterActionsByType(actions, ActionType.MATRIX_MODEL),
);

export const selectImportedMatrixModelActions = createSelector(
  [
    (state: State) => state.workflowEditor.workflowData?.actions,
  ],
  actions => filterActionsByType(actions, ActionType.IMPORTED_MATRIX_MODEL),
);

export const selectImportedOutputAttributeActions = createSelector(
  [
    (state: State) => state.workflowEditor.workflowData?.actions,
  ],
  actions => filterActionsByType(actions, ActionType.IMPORTED_OUTPUT_ATTRIBUTE),
);

export const selectServiceActions = createSelector(
  [
    (state: State) => state.workflowEditor.workflowData?.actions,
  ],
  actions => filterActionsByType(actions, ActionType.SERVICE),
);

export const selectTagActions = createSelector(
  [
    (state: State) => state.workflowEditor.workflowData?.actions,
  ],
  actions => filterActionsByType(actions, ActionType.TAG),
);
export const selectPrecedenceIndexedActions = createSelector(
  [
    (state: State) => state.workflowEditor.workflowData?.actions,
    (state: State, actionType: ActionType.OUTCOME | ActionType.WORKFLOW) => actionType,
  ],
  (actions, actionType) => filterActionsByType(actions, actionType),
);

export const selectPreviewAction = createSelector(
  [
    (state: State) => state.workflowEditor.workflowData?.actions,
  ],
  // there should only be one preview action at most
  actions => filterActionsByType(actions, ActionType.PREVIEW).at(0),
);


export const selectPreviewOutcomeAction = createSelector(
  [
    selectOutcomeActions,
  ],
  outcomeActions => outcomeActions.find(isPreviewOutcomeAction),
);

export const selectPreviewWorkflowRoutingAction = createSelector(
  [
    selectWorkflowRoutingActions,
  ],
  workflowRoutingActions => workflowRoutingActions.find(isPreviewWorkflowRoutingAction),
);

export const selectPreviewPrecedenceIndexedAction = createSelector([
  selectPreviewOutcomeAction,
  selectPreviewWorkflowRoutingAction,
], (
  previewOutcomeAction,
  previewWorkflowRoutingAction,
) => (
  /**
   * These actions will never exist in the same workflow at the same time, as workflow routing
   * actions only exist in legacy router workflows, and outcomes in every other type of workflow.
   * Therefore, it's safe to select them like this. If none exist, the selector return undefined.
   */
  previewOutcomeAction ?? previewWorkflowRoutingAction
));

export const selectPublishedAttributeActions = createSelector(
  [
    (state: State) => state.workflowEditor.workflowData?.actions,
    (state: State, actionType: ActionType.INPUT | ActionType.OUTPUT) => actionType,
  ],
  (actions, actionType) => {
    const attributeActions = filterActionsByType(actions, actionType);
    return attributeActions.filter(isPublishedAttribute);
  },
);


export function selectDefaultPrecedenceIndexedAction(state: State) {
  const { workflowType } = state.workflowEditor.workflowData;

  if (workflowType === 'router') {
    return selectDefaultWorkflowRoutingAction(state);
  }

  return selectDefaultOutcomeAction(state);
}


export function selectDefaultOutcomeAction(state: State) {
  const outcomeActions = selectOutcomeActions(state);
  return outcomeActions.find(outcomeAction => isEmpty(outcomeAction?.rules));
}


export function selectDefaultWorkflowRoutingAction(state: State) {
  const workflowRoutingActions = selectWorkflowRoutingActions(state);
  return workflowRoutingActions.find(isDefaultPrecedenceIndexedAction);
}

